import {
  Award,
  CalendarDays,
  Clock,
  CloudRain,
  Quote,
  Snowflake,
  Sun,
  Timer,
  type LucideIcon,
} from 'lucide-react'
import type { SuggestionKind } from '@/types/marketingProactive'

// Shared labels, icons and date wording for the proactive marketing screens
// (F-MKT-N1). Pure, so the date words are unit-tested.

export const KIND_LABEL: Record<SuggestionKind, string> = {
  moment: 'Calendar',
  expiring_stock: 'Stock',
  slow_hours: 'Slow hours',
  weather: 'Weather',
  review_post: 'Reviews',
  milestone: 'Regulars',
}

export function kindIcon(kind: SuggestionKind, payload?: Record<string, unknown>): LucideIcon {
  switch (kind) {
    case 'expiring_stock':
      return Timer
    case 'slow_hours':
      return Clock
    case 'weather':
      return payload?.type === 'heat' ? Sun : payload?.type === 'cold' ? Snowflake : CloudRain
    case 'review_post':
      return Quote
    case 'milestone':
      return Award
    default:
      return CalendarDays
  }
}

/** Icon tile colours per kind — quiet tints, one hue each. */
export const KIND_TONE: Record<SuggestionKind, string> = {
  moment: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  expiring_stock: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  slow_hours: 'bg-sky-500/10 text-sky-700 dark:text-sky-400',
  weather: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-400',
  review_post: 'bg-pink-500/10 text-pink-700 dark:text-pink-400',
  milestone: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
}

/** Calendar chip colours by moment kind. */
export const MOMENT_TONE: Record<string, string> = {
  religious: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300',
  national: 'bg-green-600/15 text-green-800 dark:text-green-300',
  global: 'bg-violet-500/15 text-violet-800 dark:text-violet-300',
  season: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
  commerce: 'bg-sky-500/15 text-sky-800 dark:text-sky-300',
  sport: 'bg-orange-500/15 text-orange-800 dark:text-orange-300',
  observance: 'bg-muted text-muted-foreground',
  custom: 'bg-pink-500/15 text-pink-800 dark:text-pink-300',
}

/** Solid dots for the calendar's month list. */
export const MOMENT_DOT: Record<string, string> = {
  religious: 'bg-emerald-500',
  national: 'bg-green-600',
  global: 'bg-violet-500',
  season: 'bg-amber-500',
  commerce: 'bg-sky-500',
  sport: 'bg-orange-500',
  observance: 'bg-muted-foreground/40',
  custom: 'bg-pink-500',
}

export const MOMENT_KIND_LABEL: Record<string, string> = {
  religious: 'Religious',
  national: 'National day',
  global: 'Food & global day',
  season: 'Season',
  commerce: 'Shopping & payday',
  sport: 'Sport',
  observance: 'Observance',
  custom: 'Your moment',
}

export const BUSINESS_TYPE_LABEL: Record<string, string> = {
  cafe: 'Café',
  desi: 'Desi restaurant',
  fast_food: 'Fast food',
  bakery: 'Bakery & desserts',
  fine_dining: 'Fine dining',
  caterer: 'Caterer',
}

/** Parse YYYY-MM-DD as a local calendar date (no timezone drift). */
export function parseDay(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, (m || 1) - 1, d || 1)
}

export function toISODay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** "31 Oct" — or "31 Oct 2027" when not this year. */
export function shortDate(iso: string, thisYear?: number): string {
  const d = parseDay(iso)
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
  if (thisYear != null && d.getFullYear() !== thisYear) opts.year = 'numeric'
  return d.toLocaleDateString('en-GB', opts)
}

/** "31 Oct" / "4–6 Nov" / "28 Nov – 3 Dec". */
export function dateRange(from: string, to: string): string {
  if (from === to) return shortDate(from)
  const a = parseDay(from)
  const b = parseDay(to)
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
    return `${a.getDate()}–${shortDate(to)}`
  }
  return `${shortDate(from)} – ${shortDate(to)}`
}

/** "today", "tomorrow", "in 6 days", "on now". */
export function daysAway(days: number): string {
  if (days < 0) return 'on now'
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  return `in ${days} days`
}
